import Link from 'next/link';
import pluralize from 'pluralize';
import { memo } from 'react';

import { ChevronRight, Info } from '@air/next-icons';
import { Tooltip } from '@air/primitive-tooltip';

import { MemberInfo } from '~/components/MemberInfo/MemberInfo';
import { MemberListRow } from '~/components/MemberListRow';
import { MemberListMemberRole } from '~/components/Members/MemberListMemberRole';
import { Routes } from '~/constants/routes';
import { useWorkspaceMembers } from '~/swr-hooks/members/useWorkspaceMembers';

type MembersListProps = {
  onClickViewAllUsers: () => void;
};

export const MembersList = memo(({ onClickViewAllUsers }: MembersListProps) => {
  const { members } = useWorkspaceMembers();

  if (!members) {
    return null;
  }

  return (
    <>
      <h3 className="flex justify-between font-semibold text-14 text-pigeon-500">
        <div className="flex items-center gap-1">
          {pluralize('user', members.allUsersWithMostPermissiveRole.length, true)}
          <Tooltip
            className="max-w-[220px] text-12"
            label="Some users listed here have access to boards only, but may still count toward your paid seat limit."
          >
            <Info className="size-4" />
          </Tooltip>
        </div>
        <Link href={Routes.workspaceSettings.members} className="flex items-center gap-1" onClick={onClickViewAllUsers}>
          View all users <ChevronRight className="size-4" />
        </Link>
      </h3>
      <div>
        {members.allUsersWithMostPermissiveRole.map((member) => (
          <MemberListRow key={member.email}>
            <MemberInfo className="flex-auto" member={member} />
            <MemberListMemberRole
              className="-mr-2 flex-none px-0"
              member={member}
              noWorkspaceRoleLabel={<div className="mr-3 whitespace-nowrap">Board only access</div>}
            />
          </MemberListRow>
        ))}
      </div>
    </>
  );
});

MembersList.displayName = 'MembersList';
